import { useCallback, useEffect, useMemo, useRef } from 'react';
import Globe, { GlobeMethods } from 'react-globe.gl';
import type { RouteItem } from '@/types/routeMap';
import { makeRouteColorScale } from './colorScale';

interface GlobeCanvasProps {
  routes: RouteItem[];
  width: number;
  height: number;
  autoRotate?: boolean;
}

interface PointDatum {
  code: string;
  lat: number;
  lng: number;
  totalCount: number;
}

const EARTH_TEXTURE = '/textures/earth-night.jpg';
const BUMP_TEXTURE = '/textures/earth-topology.png';

function setupControls(globe: GlobeMethods | undefined, autoRotate: boolean) {
  if (!globe) return;
  const controls = globe.controls();
  if (!controls) return;
  controls.autoRotate = autoRotate;
  controls.autoRotateSpeed = 0.3;
  controls.enableDamping = true;
}

export function GlobeCanvas({ routes, width, height, autoRotate = true }: GlobeCanvasProps) {
  const globeRef = useRef<GlobeMethods | undefined>(undefined);

  const drawableRoutes = useMemo(
    () => routes.filter((r) => r.departureCoord && r.arrivalCoord),
    [routes],
  );

  const colorScale = useMemo(() => {
    const maxCount = drawableRoutes.reduce((m, r) => Math.max(m, r.count), 1);
    return makeRouteColorScale(maxCount);
  }, [drawableRoutes]);

  const points = useMemo<PointDatum[]>(() => {
    const map = new Map<string, PointDatum>();
    for (const r of drawableRoutes) {
      if (r.departureCoord) {
        const k = r.departure;
        const cur = map.get(k);
        map.set(k, {
          code: k,
          lat: r.departureCoord.lat,
          lng: r.departureCoord.lng,
          totalCount: (cur?.totalCount ?? 0) + r.count,
        });
      }
      if (r.arrivalCoord) {
        const k = r.arrival;
        const cur = map.get(k);
        map.set(k, {
          code: k,
          lat: r.arrivalCoord.lat,
          lng: r.arrivalCoord.lng,
          totalCount: (cur?.totalCount ?? 0) + r.count,
        });
      }
    }
    return Array.from(map.values());
  }, [drawableRoutes]);

  const handleGlobeReady = useCallback(() => {
    setupControls(globeRef.current, autoRotate);
  }, [autoRotate]);

  useEffect(() => {
    setupControls(globeRef.current, autoRotate);
  }, [autoRotate]);

  return (
    <Globe
      ref={globeRef}
      width={width}
      height={height}
      globeImageUrl={EARTH_TEXTURE}
      bumpImageUrl={BUMP_TEXTURE}
      backgroundColor="rgba(0,0,0,0)"
      atmosphereColor="#3aa3ff"
      atmosphereAltitude={0.18}
      arcsData={drawableRoutes}
      arcStartLat={(d: object) => (d as RouteItem).departureCoord!.lat}
      arcStartLng={(d: object) => (d as RouteItem).departureCoord!.lng}
      arcEndLat={(d: object) => (d as RouteItem).arrivalCoord!.lat}
      arcEndLng={(d: object) => (d as RouteItem).arrivalCoord!.lng}
      arcColor={(d: object) => colorScale((d as RouteItem).count)}
      arcStroke={(d: object) => 0.4 + Math.log2((d as RouteItem).count + 1) * 0.15}
      arcAltitudeAutoScale={0.5}
      arcDashLength={0.4}
      arcDashGap={2}
      arcDashAnimateTime={(d: object) => 5000 - Math.min((d as RouteItem).count * 100, 3000)}
      arcsTransitionDuration={800}
      pointsData={points}
      pointLat={(d: object) => (d as PointDatum).lat}
      pointLng={(d: object) => (d as PointDatum).lng}
      pointColor={() => '#7dd3fc'}
      pointAltitude={0.005}
      pointRadius={(d: object) => Math.min(0.6, 0.15 + Math.sqrt((d as PointDatum).totalCount) * 0.05)}
      pointLabel={(d: object) => `${(d as PointDatum).code} · ${(d as PointDatum).totalCount} 次`}
      pointsTransitionDuration={800}
      onGlobeReady={handleGlobeReady}
    />
  );
}
